/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useId, useState } from 'react'
import Highcharts from 'highcharts'
import HC_data from 'highcharts/modules/data'
import { DonutChartData } from '../../types/StatTypes'
import ChartSkeleton from '../skeleton/ChartSkeleton'
import BaseChart from './BaseChart'
interface IDonutChartProps {
  chartData: DonutChartData
}

interface DonutPoint extends Highcharts.Point {
  parentName: string
  raw: number
}

const DonutChart = (props: IDonutChartProps) => {
  const { chartData } = props
  const [isLoading, setIsLoading] = useState(true)
  const chartId = useId()

  useEffect(() => {
    if (typeof HC_data === 'function') {
      HC_data(Highcharts)
    }
  }, [])

  useEffect(() => {
    if (chartData.data && chartData.data?.length > 0) {
      setIsLoading(false)
      const timer = setTimeout(() => setIsLoading(false), 400)
      return () => clearTimeout(timer)
    }
  }, [chartData])
  const categories = chartData.categories
  const data = chartData.data
  const parentData = [],
    childData = [],
    dataLen = data?.length

  let i, j, drillDataLen, brightness
  for (i = 0; i < dataLen; i += 1) {
    parentData.push({
      name: categories[i],
      y: data[i].y,
      raw: data[i].raw,
      color: data[i].color,
    })

    drillDataLen = data[i].drilldown.data?.length
    for (j = 0; j < drillDataLen; j += 1) {
      const name = data[i].drilldown.categories[j]
      brightness = 0.2 - j / drillDataLen / 5
      childData.push({
        parentName: categories[i],
        name,
        y: data[i].drilldown.data[j].y,
        raw: data[i].drilldown.data[j].raw,
        color: Highcharts.color(data[i].color).brighten(brightness).get(),
        custom: {
          version: name?.split(' ')[1] || name?.split(' ')[0],
        },
      })
    }
  }

  const options: any = {
    chart: {
      type: 'pie',
      height: chartData.height ?? 300,
      backgroundColor: 'transparent',
    },
    plotOptions: {
      pie: {
        shadow: false,
        center: ['50%', '50%'],
        showInLegend: false,
      },
    },
    title: {
      text: null,
    },
    tooltip: {
      headerFormat: '',
      pointFormatter: function (this: DonutPoint): string {
        const rawValue = (this as any).raw
        return `<span style="color:${this.color}">\u25CF</span> ${this.parentName ?? ''} ${this.name}: <b>${rawValue} (${this.y}%)</b><br/>`
      },
    },
    series: [
      {
        name: chartData.name,
        data: parentData,
        size: '45%',
        dataLabels: {
          color: '#ffffff',
          distance: '-50%',
        },
        showInLegend: false,
      },
      {
        name: chartData.name,
        data: childData,
        size: '80%',
        innerSize: '60%',
        dataLabels: {
          format: '<b>{point.parentName} {point.name}:</b> <span style="opacity: 0.5">' + '{y}%</span>',
          filter: {
            property: 'y',
            operator: '>',
            value: 1,
          },
          style: {
            fontWeight: 'normal',
          },
          color: chartData.textColor,
        },
        id: 'children',
        legend: {
          enabled: true,
        },
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            series: [
              {},
              {
                id: 'children',
                dataLabels: {
                  enabled: false,
                },
                showInLegend: true,
              },
            ],
            legend: {
              enabled: true,
              itemStyle: {
                color: chartData.textColor,
              },
              itemHoverStyle: {
                color: chartData.textColor,
              },
              align: 'center',
              verticalAlign: 'bottom',
              layout: 'horizontal',
              labelFormatter: function (this: DonutPoint): string {
                return `${this.parentName} ${this.name} : ${this.raw} (${this.y}%)`
              },
            },
          },
        },
      ],
    },

    credits: {
      enabled: false,
    },
  }

  return <>{isLoading ? <ChartSkeleton fullWidth={true} /> : <BaseChart options={options} id={chartId} />}</>
}

export default DonutChart
